import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { emojiCategories } from "./emoji-data.js";
import { css, html, nothing } from "lit";
import { property, state } from "lit/decorators.js";
import { msg } from "@lit/localize";
import "@progressive-development/pd-icon/pd-icon";
import "@progressive-development/pd-forms/pd-input";
import { pdIcons } from "@progressive-development/pd-icon";
import { brandIconMap } from "@progressive-development/pd-icon-brands";
import { classMap } from "lit/directives/class-map.js";
import { PdBaseUIInput } from "@progressive-development/pd-forms";
import { eventIconMap } from "@progressive-development/pd-icon-events";
//#region src/pd-icon-picker/PdIconPicker.ts
/**
* @license
* Copyright (c) 2024 PD Progressive Development UG. All rights reserved.
*/
var RECENT_DUMMY = [
	{
		value: "emoji:full-bullseye",
		label: "Ziel",
		tags: "target ziel",
		type: "emoji",
		emoji: "🎯"
	},
	{
		value: "emoji:full-calendar-date",
		label: "Kalender",
		tags: "calendar kalender",
		type: "emoji",
		emoji: "📅"
	},
	{
		value: "emoji:full-busts-silhouette",
		label: "Personen",
		tags: "people personen",
		type: "emoji",
		emoji: "👥"
	},
	{
		value: "emoji:full-trophy",
		label: "Pokal",
		tags: "trophy pokal",
		type: "emoji",
		emoji: "🏆"
	},
	{
		value: "emoji:full-high-voltage",
		label: "Blitz",
		tags: "lightning blitz",
		type: "emoji",
		emoji: "⚡"
	},
	{
		value: "emoji:full-rocket",
		label: "Rakete",
		tags: "rocket rakete",
		type: "emoji",
		emoji: "🚀"
	}
];
function buildEmojiSections() {
	return emojiCategories.map((cat) => ({
		id: cat.id,
		label: cat.label,
		items: cat.items.map((e) => ({
			value: `emoji:${e.key}`,
			label: e.key,
			tags: e.tags,
			type: "emoji",
			emoji: e.emoji
		}))
	}));
}
/** PD Icons that make sense in a picker context (skip internal/UI-only ones). */
var PICKER_PD_ICONS = [
	"starIcon",
	"userIcon",
	"settingsIcon",
	"statIcon",
	"dateIcon",
	"dateTimeIcon",
	"phoneIcon",
	"mailIcon",
	"linkIcon",
	"editIcon",
	"editIcon2",
	"searchIcon",
	"addIcon",
	"deleteIcon",
	"copyIcon",
	"syncIcon",
	"detailIcon",
	"cameraIcon",
	"rulesIcon",
	"circleIcon",
	"aiSparkleIcon",
	"infoIcon",
	"helpIcon",
	"warningIcon",
	"errorIcon"
];
function buildPdIconSection() {
	return {
		id: "pd-icons",
		label: "PD Icons",
		items: PICKER_PD_ICONS.map((name) => ({
			value: `icon:${name}`,
			label: name.replace(/Icon\d?$/, "").replace(/([A-Z])/g, " $1").trim().toLowerCase(),
			tags: name.replace(/Icon\d?$/, "").replace(/([A-Z])/g, " $1").trim().toLowerCase(),
			type: "icon",
			iconName: name
		}))
	};
}
function buildBrandSection() {
	const seen = /* @__PURE__ */ new Set();
	const items = [];
	for (const [name] of brandIconMap) {
		if (seen.has(name) || name === "twitter" || name === "x" || name === "apple-music") continue;
		seen.add(name);
		items.push({
			value: `brand:${name}`,
			label: name,
			tags: name,
			type: "brand",
			iconName: name
		});
	}
	return {
		id: "brand-icons",
		label: "Brand Icons",
		items
	};
}
function buildEventSection() {
	const items = [];
	for (const [name] of eventIconMap) items.push({
		value: `event:${name}`,
		label: name,
		tags: name,
		type: "event",
		iconName: name
	});
	return {
		id: "event-icons",
		label: "Event Icons",
		items
	};
}
function buildAllSections() {
	return [
		...buildEmojiSections(),
		buildPdIconSection(),
		buildBrandSection(),
		buildEventSection()
	];
}
var _allSections = null;
function getAllSections() {
	if (!_allSections) _allSections = buildAllSections();
	return _allSections;
}
function findItemByValue(value) {
	for (const section of getAllSections()) {
		const found = section.items.find((i) => i.value === value);
		if (found) return found;
	}
}
/**
* Icon/Emoji picker component with popup grid.
*
* Displays a trigger showing the selected icon. On click, opens a popup
* with categorized, searchable icon/emoji grid. Desktop: absolute popup.
* Mobile: fullscreen modal with backdrop.
*
* @tagname pd-icon-picker
* @summary Visual icon/emoji picker with search and categorized grid.
*
* @event pd-form-element-change - Fired when an icon is selected. Detail: `{ value: string }`.
*
* @cssprop --pd-icon-picker-width - Trigger width. Default: `250px`.
* @cssprop --pd-icon-picker-popup-width - Popup width (desktop). Default: `340px`.
* @cssprop --pd-icon-picker-popup-max-height - Popup max height (desktop). Default: `400px`.
* @cssprop --pd-icon-picker-columns - Grid columns. Default: `8`.
* @cssprop --pd-icon-picker-cell-size - Grid cell size. Default: `36px`.
*/
var PdIconPicker = class extends PdBaseUIInput {
	constructor(..._args) {
		super(..._args);
		this.placeHolder = "";
		this.modal = false;
		this.modalBreakpoint = 480;
		this._open = false;
		this._useModal = false;
		this._searchQuery = "";
		this._popupPosition = "bottom";
	}
	static {
		this.styles = [PdBaseUIInput.styles, css`
      :host {
        position: relative;
        display: inline-block;
      }

      :host([disabled]) {
        pointer-events: none;
        opacity: 0.5;
      }

      /* ── Trigger ──────────────────────────────────────── */

      .trigger {
        display: flex;
        align-items: center;
        gap: var(--pd-spacing-xs, 0.25rem);
        width: var(--pd-icon-picker-width, var(--pd-input-field-width, 250px));
        max-width: 100%;
        min-width: 150px;
        height: var(--pd-input-field-height, 40px);
        padding: 0 var(--pd-spacing-sm, 0.5rem);
        border: var(--pd-input-border, 1px solid var(--pd-default-col, #ccc));
        border-radius: var(--pd-radius-sm, 4px);
        background: var(
          --pd-input-field-bg-col,
          var(--pd-default-bg-col, #fff)
        );
        cursor: pointer;
        box-sizing: border-box;
        transition: border-color 0.2s;
      }

      .trigger:hover {
        border-color: var(--pd-input-border-hover-col, var(--pd-default-col));
      }

      .trigger:focus-visible {
        outline: 2px solid var(--pd-focus-ring-col, #067394);
        outline-offset: 2px;
      }

      .trigger-icon {
        font-size: 1.5rem;
        line-height: 1;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2rem;
        height: 2rem;
      }

      .trigger-icon svg {
        width: 1.25rem;
        height: 1.25rem;
        fill: currentColor;
      }

      .trigger-icon pd-icon {
        --pd-icon-size: 1.25rem;
      }

      .trigger-placeholder {
        color: var(--pd-input-placeholder-col, #999);
        font-size: var(--pd-input-font-size, 0.9em);
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .trigger-clear {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        border: none;
        background: none;
        cursor: pointer;
        color: var(--pd-default-font-col, #666);
        padding: 0;
        border-radius: 50%;
        transition: background-color 0.2s;
      }

      .trigger-clear:hover {
        background-color: var(--pd-default-bg-col-hover, rgba(0, 0, 0, 0.08));
      }

      /* ── Popup ────────────────────────────────────────── */

      .popup {
        position: absolute;
        background-color: var(--pd-default-bg-col, #fff);
        visibility: hidden;
        z-index: var(--pd-icon-picker-z-index, 1000);
        box-shadow: var(
          --pd-menu-shadow,
          var(--pd-shadow-lg, 0 4px 24px rgba(0, 0, 0, 0.15))
        );
        opacity: 0;
        transition: opacity 0.2s ease-out;
        border-radius: var(--pd-radius-md, 8px);
        width: var(--pd-icon-picker-popup-width, 340px);
        max-width: calc(100vw - 1rem);
        overflow: hidden;
        display: flex;
        flex-direction: column;
      }

      :host .popup.open {
        visibility: visible;
        opacity: 1;
      }

      .popup.bottom {
        top: 100%;
        margin-top: 4px;
      }

      .popup.top {
        bottom: 100%;
        margin-bottom: 4px;
      }

      /* ── Modal mode ───────────────────────────────────── */

      .modal-backdrop {
        display: none;
        position: fixed;
        inset: 0;
        background-color: var(--pd-modal-overlay-col, rgba(0, 0, 0, 0.5));
        z-index: calc(var(--pd-icon-picker-z-index, 1000) - 1);
      }

      .modal-backdrop.active {
        display: block;
      }

      .popup.modal {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        margin: 0;
        width: calc(100vw - 2rem);
        max-width: 400px;
        max-height: 85vh;
        border-radius: var(--pd-radius-lg, 12px);
      }

      /* ── Search ───────────────────────────────────────── */

      .search-wrapper {
        padding: var(--pd-spacing-sm, 0.5rem);
        border-bottom: 1px solid var(--pd-default-border-col, #e5e7eb);
      }

      .search-wrapper pd-input {
        --pd-input-field-width: 100%;
      }

      /* ── Scroll area ──────────────────────────────────── */

      .scroll-area {
        overflow-y: auto;
        max-height: var(--pd-icon-picker-popup-max-height, 400px);
        padding: var(--pd-spacing-xs, 0.25rem) var(--pd-spacing-sm, 0.5rem);
      }

      .popup.modal .scroll-area {
        max-height: calc(85vh - 60px); /* subtract search area */
      }

      /* ── Section headers ──────────────────────────────── */

      .section-header {
        font-size: 0.7rem;
        font-weight: 600;
        color: var(--pd-default-font-secondary-col, #6b7280);
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 0.5rem 0 0.25rem 0.25rem;
        margin: 0;
      }

      .section-header:first-child {
        padding-top: 0.25rem;
      }

      /* ── Grid ─────────────────────────────────────────── */

      .icon-grid {
        display: grid;
        grid-template-columns: repeat(
          var(--pd-icon-picker-columns, 8),
          var(--pd-icon-picker-cell-size, 36px)
        );
        gap: 2px;
      }

      .icon-cell {
        display: flex;
        align-items: center;
        justify-content: center;
        width: var(--pd-icon-picker-cell-size, 36px);
        height: var(--pd-icon-picker-cell-size, 36px);
        border-radius: var(--pd-radius-sm, 4px);
        cursor: pointer;
        border: none;
        background: none;
        padding: 0;
        font-size: 1.35rem;
        line-height: 1;
        transition: background-color 0.15s;
        color: var(--pd-default-font-col, inherit);
      }

      .icon-cell:hover {
        background-color: var(--pd-default-bg-col-hover, rgba(0, 0, 0, 0.06));
      }

      .icon-cell:focus-visible {
        outline: 2px solid var(--pd-focus-ring-col, #067394);
        outline-offset: -2px;
      }

      .icon-cell.selected {
        background-color: var(--pd-default-col-light, rgba(6, 115, 148, 0.12));
      }

      .icon-cell svg {
        width: 1.1rem;
        height: 1.1rem;
        fill: currentColor;
      }

      .icon-cell pd-icon {
        --pd-icon-size: 1.1rem;
      }

      /* ── Clear button ─────────────────────────────────── */

      .clear-row {
        border-top: 1px solid var(--pd-default-border-col, #e5e7eb);
        padding: var(--pd-spacing-xs, 0.25rem) var(--pd-spacing-sm, 0.5rem);
      }

      .clear-btn {
        display: flex;
        align-items: center;
        gap: 0.25rem;
        width: 100%;
        border: none;
        background: none;
        cursor: pointer;
        padding: 0.4rem 0.25rem;
        font-size: 0.8rem;
        color: var(--pd-default-font-secondary-col, #6b7280);
        border-radius: var(--pd-radius-sm, 4px);
        transition: background-color 0.15s;
      }

      .clear-btn:hover {
        background-color: var(--pd-default-bg-col-hover, rgba(0, 0, 0, 0.06));
      }

      /* ── Empty state ──────────────────────────────────── */

      .empty-state {
        padding: 2rem 1rem;
        text-align: center;
        color: var(--pd-default-font-secondary-col, #6b7280);
        font-size: 0.85rem;
      }
    `];
	}
	render() {
		const selectedItem = this._value ? findItemByValue(this._value) : void 0;
		return html`
      ${this._renderPickerLabel()} ${this._renderTrigger(selectedItem)}
      ${this._renderErrorMsg()}
      <div
        class="modal-backdrop ${this._open && this._useModal ? "active" : ""}"
        @click="${this._close}"
      ></div>
      ${this._renderPopup(selectedItem)}
    `;
	}
	_renderTrigger(selectedItem) {
		const popupId = `${this.id || "icon-picker"}-popup`;
		return html`
      <div
        class="trigger"
        tabindex="0"
        role="combobox"
        aria-haspopup="dialog"
        aria-expanded="${this._open}"
        aria-controls="${popupId}"
        aria-label="${this.label || msg("Icon auswählen", { id: "pd.iconPicker.selectIcon" })}"
        @click="${this._togglePopup}"
        @keydown="${this._onTriggerKeyDown}"
      >
        ${selectedItem ? html`
                <span class="trigger-icon"
                  >${this._renderItemContent(selectedItem)}</span
                >
                <span
                  class="trigger-placeholder"
                  style="color: var(--pd-default-font-col, inherit)"
                >
                  ${selectedItem.label}
                </span>
                <button
                  class="trigger-clear"
                  tabindex="-1"
                  aria-label="${msg("Icon entfernen", { id: "pd.iconPicker.clearIcon" })}"
                  @click="${this._clearValue}"
                >
                  &times;
                </button>
              ` : html`
                <span class="trigger-placeholder">
                  ${this.placeHolder || msg("Icon auswählen...", { id: "pd.iconPicker.placeholder" })}
                </span>
              `}
      </div>
    `;
	}
	_renderPopup(selectedItem) {
		const popupId = `${this.id || "icon-picker"}-popup`;
		const sections = this._getFilteredSections();
		const hasResults = sections.some((s) => s.items.length > 0);
		return html`
      <div
        id="${popupId}"
        class="popup ${classMap({
			open: this._open,
			modal: this._useModal,
			top: !this._useModal && this._popupPosition === "top",
			bottom: !this._useModal && this._popupPosition === "bottom"
		})}"
        role="dialog"
        aria-modal="true"
        aria-label="${msg("Icon auswählen", { id: "pd.iconPicker.dialogLabel" })}"
        @keydown="${this._onPopupKeyDown}"
      >
        <div class="search-wrapper">
          <pd-input
            class="search-input"
            no-label
            .icon="${pdIcons.ICON_SEARCH}"
            icon-right
            .placeHolder="${msg("Suchen...", { id: "pd.iconPicker.search" })}"
            .initValue="${this._searchQuery}"
            @pd-form-element-change="${this._onSearchInput}"
            @pd-form-element-register="${this._stopInnerEvent}"
            @pd-form-element-blur="${this._stopInnerEvent}"
            @pd-form-element-focus="${this._stopInnerEvent}"
          ></pd-input>
        </div>
        <div class="scroll-area">
          ${hasResults ? this._renderSections(sections, selectedItem) : html`<div class="empty-state">
                  ${msg("Nichts gefunden", { id: "pd.iconPicker.noResults" })}
                </div>`}
        </div>
        <div class="clear-row">
          <button class="clear-btn" @click="${this._clearAndClose}">
            &times; ${msg("Kein Icon", { id: "pd.iconPicker.noIcon" })}
          </button>
        </div>
      </div>
    `;
	}
	_renderSections(sections, selectedItem) {
		const recentSection = !this._searchQuery ? html`
          <p class="section-header">
            ${msg("Zuletzt verwendet", { id: "pd.iconPicker.recent" })}
          </p>
          <div class="icon-grid">
            ${RECENT_DUMMY.map((item) => this._renderCell(item, selectedItem))}
          </div>
        ` : nothing;
		return html`
      ${recentSection}
      ${sections.map((section) => section.items.length > 0 ? html`
              <p class="section-header">${section.label}</p>
              <div class="icon-grid">
                ${section.items.map((item) => this._renderCell(item, selectedItem))}
              </div>
            ` : nothing)}
    `;
	}
	_renderCell(item, selectedItem) {
		const isSelected = selectedItem?.value === item.value;
		return html`
      <button
        class="icon-cell ${isSelected ? "selected" : ""}"
        title="${item.label}"
        aria-label="${item.label}"
        aria-pressed="${isSelected}"
        @click="${() => this._selectItem(item)}"
      >
        ${this._renderItemContent(item)}
      </button>
    `;
	}
	_renderItemContent(item) {
		switch (item.type) {
			case "emoji": return item.emoji;
			case "icon": return html`<pd-icon .icon="${item.iconName}"></pd-icon>`;
			case "brand": return brandIconMap.get(item.iconName) ?? nothing;
			case "event": return eventIconMap.get(item.iconName) ?? nothing;
		}
	}
	_renderPickerLabel() {
		return this._renderLabel("icon-picker-trigger");
	}
	_getFilteredSections() {
		const query = this._searchQuery.toLowerCase().trim();
		if (!query) return getAllSections();
		const terms = query.split(/\s+/);
		return getAllSections().map((section) => ({
			...section,
			items: section.items.filter((item) => terms.every((term) => item.tags.includes(term) || item.label.includes(term)))
		}));
	}
	_togglePopup(e) {
		if (e.target.closest(".trigger-clear")) return;
		if (this._open) this._close();
		else this._openPopup();
	}
	_openPopup() {
		this._searchQuery = "";
		this._useModal = this.modal || window.innerWidth < this.modalBreakpoint;
		if (!this._useModal) {
			const trigger = this.shadowRoot?.querySelector(".trigger");
			if (trigger) {
				const rect = trigger.getBoundingClientRect();
				const spaceBelow = window.innerHeight - rect.bottom;
				this._popupPosition = spaceBelow < 420 && rect.top > spaceBelow ? "top" : "bottom";
			}
		}
		if (this._useModal) document.body.style.overflow = "hidden";
		this._open = true;
		this.updateComplete.then(() => {
			(this.shadowRoot?.querySelector(".search-input"))?.focus();
		});
		if (!this._useModal) setTimeout(() => {
			this._boundHandleClickOutside = this._handleClickOutside.bind(this);
			document.addEventListener("click", this._boundHandleClickOutside);
		}, 0);
	}
	_close() {
		this._open = false;
		this._searchQuery = "";
		if (this._useModal) document.body.style.overflow = "";
		if (this._boundHandleClickOutside) {
			document.removeEventListener("click", this._boundHandleClickOutside);
			this._boundHandleClickOutside = void 0;
		}
		(this.shadowRoot?.querySelector(".trigger"))?.focus();
	}
	_selectItem(item) {
		this._handleChangedValue(item.value, void 0, true);
		this._close();
	}
	_clearValue(e) {
		e.stopPropagation();
		this._handleChangedValue("", void 0, true);
	}
	_clearAndClose() {
		this._handleChangedValue("", void 0, true);
		this._close();
	}
	_onSearchInput(e) {
		e.stopPropagation();
		this._searchQuery = e.detail?.value ?? "";
	}
	_stopInnerEvent(e) {
		e.stopPropagation();
	}
	_onTriggerKeyDown(e) {
		switch (e.key) {
			case " ":
			case "Enter":
				e.preventDefault();
				if (this._open) this._close();
				else this._openPopup();
				break;
			case "ArrowDown":
				e.preventDefault();
				if (!this._open) this._openPopup();
				break;
			case "Escape": if (this._open) {
				e.preventDefault();
				this._close();
			}
		}
	}
	_onPopupKeyDown(e) {
		if (e.key === "Escape") {
			e.preventDefault();
			e.stopPropagation();
			this._close();
		}
	}
	_handleClickOutside(e) {
		if (!e.composedPath().includes(this)) this._close();
	}
	disconnectedCallback() {
		if (this._boundHandleClickOutside) {
			document.removeEventListener("click", this._boundHandleClickOutside);
			this._boundHandleClickOutside = void 0;
		}
		if (this._useModal && this._open) document.body.style.overflow = "";
		super.disconnectedCallback();
	}
};
__decorate([property({ type: String })], PdIconPicker.prototype, "placeHolder", void 0);
__decorate([property({ type: Boolean })], PdIconPicker.prototype, "modal", void 0);
__decorate([property({ type: Number })], PdIconPicker.prototype, "modalBreakpoint", void 0);
__decorate([state()], PdIconPicker.prototype, "_open", void 0);
__decorate([state()], PdIconPicker.prototype, "_useModal", void 0);
__decorate([state()], PdIconPicker.prototype, "_searchQuery", void 0);
__decorate([state()], PdIconPicker.prototype, "_popupPosition", void 0);
//#endregion
export { PdIconPicker };
